import { useEffect, useState, type FormEvent } from 'react';
import {
  DEFAULT_TRAINING_INSTRUCTIONS,
  TrainingInstructionsSchema,
  type TrainingDefaults,
  type TrainingInstructions,
} from '@allemnii-pulse/shared';
import { useDocument } from '../../lib/firestore.js';
import { api, ApiError } from '../../lib/api.js';
import { relativeTime } from '../../lib/time.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { UserName } from '../../components/UserName.js';
import { InstructionsFields } from './InstructionsFields.js';
import { useTrainingState } from './useTraining.js';

function InstructionsForm({
  idPrefix,
  title,
  description,
  value,
  canManage,
  meta,
  onSave,
}: {
  idPrefix: string;
  title: string;
  description: string;
  value: TrainingInstructions;
  canManage: boolean;
  meta?: React.ReactNode;
  onSave: (instructions: TrainingInstructions) => Promise<void>;
}) {
  const [form, setForm] = useState(value);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live updates stop overwriting the form once the user starts editing — see DevPreferencesCard.
  useEffect(() => {
    if (!dirty) setForm(value);
  }, [value, dirty]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <form onSubmit={(e) => void handleSave(e)}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
          {meta}
        </CardHeader>
        <CardContent>
          <InstructionsFields
            idPrefix={idPrefix}
            value={form}
            disabled={!canManage}
            onChange={(next) => {
              setForm(next);
              setDirty(true);
            }}
          />
          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </CardContent>
        {canManage && (
          <CardFooter className="justify-end">
            <Button type="submit" variant="default" disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </CardFooter>
        )}
      </form>
    </Card>
  );
}

/** Org settings: the generation and design instructions every new training project starts from. */
export function TrainingDefaultsCard({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const { data } = useDocument<TrainingDefaults>(`orgs/${orgId}/settings/trainingDefaults`);
  const parsed = TrainingInstructionsSchema.safeParse(data ?? {});
  const value = parsed.success ? parsed.data : DEFAULT_TRAINING_INSTRUCTIONS;
  return (
    <InstructionsForm
      idPrefix="org-training"
      title="Training slides"
      description="Default generation instructions (the training's style: technical, funny, serious…) and design for new training projects. Each project keeps its own copy, and each generation can be customised."
      value={value}
      canManage={canManage}
      meta={
        data?.updatedAt ? (
          <span className="pt-1 text-xs text-muted">
            Updated {relativeTime(data.updatedAt)}
            {data.updatedByUid && (
              <>
                {' '}
                by <UserName uid={data.updatedByUid} />
              </>
            )}
          </span>
        ) : (
          <span className="pt-1 text-xs text-muted">Using the built-in defaults.</span>
        )
      }
      onSave={(instructions) => api.put(`/orgs/${orgId}/settings/training-defaults`, instructions)}
    />
  );
}

/** Project settings (training projects): this project's own instructions, copied from the org's
 * defaults when it was created. */
export function ProjectTrainingInstructionsCard({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) {
  const { data: state } = useTrainingState(projectId);
  if (!state) return null;
  return (
    <InstructionsForm
      idPrefix="project-training"
      title="Training slides"
      description="This training's generation instructions and design. Used by every generation unless customised for that run. Slides already generated keep the design they were made with."
      value={state.instructions}
      canManage={canManage}
      onSave={(instructions) =>
        api.patch(`/projects/${projectId}/training/settings`, { instructions })
      }
    />
  );
}
